/**
 * Password Utility Functions
 * Shared password generation and validation across mobile and web
 */

import { PASSWORD_CONFIG } from '../constants/app';

/**
 * Generates a secure random password for invited users
 * - Configurable length (default: 12 characters from PASSWORD_CONFIG)
 * - Contains at least one uppercase, lowercase, number, and symbol
 * - Characters are randomized for security
 *
 * @returns A randomly generated secure password
 */
export function generatePassword(): string {
  const { UPPERCASE, LOWERCASE, NUMBERS, SYMBOLS } = PASSWORD_CONFIG.CHARSET;
  const allChars = UPPERCASE + LOWERCASE + NUMBERS + SYMBOLS;
  const length = PASSWORD_CONFIG.GENERATED_LENGTH;

  let password = '';

  // Ensure at least one character from each category
  password += UPPERCASE[Math.floor(Math.random() * UPPERCASE.length)];
  password += LOWERCASE[Math.floor(Math.random() * LOWERCASE.length)];
  password += NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
  password += SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];

  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password to randomize character positions
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

/**
 * Validates password meets minimum requirements
 *
 * @param password - Password to validate
 * @returns true if password is valid, false otherwise
 */
export function validatePassword(password: string): boolean {
  return password.length >= PASSWORD_CONFIG.MIN_LENGTH;
}

/**
 * Gets validation error message for password
 *
 * @param password - Password to validate
 * @returns Error message if invalid, null if valid
 */
export function getPasswordError(password: string): string | null {
  if (!password) return 'Password is required';
  if (!validatePassword(password)) {
    return `Password must be at least ${PASSWORD_CONFIG.MIN_LENGTH} characters`;
  }
  return null;
}
